import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const CONTACT_EMAIL = "training@emlsynergy.com";
const DEMO_REQUEST_RECIPIENTS = ["manimozhi@emlsynergy.com", "kumudha@emlsynergy.com", "edeesdar@emlsynergy.com"];

const INPUT_CLASS =
    "w-full border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-[#7FD8C4]";

const Field = ({ label, htmlFor, children }) => (
    <div>
        <label
            htmlFor={htmlFor}
            className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400"
        >
            {label}
        </label>
        {children}
    </div>
);

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    // No backend: hand the request off to the visitor's own email client.
    const submit = (e) => {
        e.preventDefault();
        const subject = `Demo request — ${form.name}`;
        const body = [
            `Name: ${form.name}`,
            `Email: ${form.email}`,
            `Company: ${form.company || "—"}`,
            "",
            form.message,
        ].join("\n");
        window.location.href =
            `mailto:${DEMO_REQUEST_RECIPIENTS.join(",")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return (
        <section id="contact" data-testid="contact-section" className="bg-[#FAFAFA] py-24 lg:py-36">
            <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
                <div>
                    <Reveal>
                        <p className="flex items-center gap-4 text-xs font-bold uppercase tracking-[0.3em] text-[#1FA187]">
                            <span className="h-px w-10 bg-[#1FA187]" />
                            Get in touch
                        </p>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="font-display mt-6 text-4xl font-bold tracking-tight text-[#0A1B3F] sm:text-5xl">
                            Ready to see it running on your team's culture?
                        </h2>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="mt-8 max-w-lg text-base leading-relaxed text-slate-600 lg:text-lg">
                            Drop us a line and we'll set up a walk-through tailored to your org. Bring your
                            hardest people-and-culture question — we've probably built for it.
                        </p>
                        <a
                            data-testid="contact-email-link"
                            href={`mailto:${CONTACT_EMAIL}`}
                            className="group font-display mt-10 inline-flex items-center gap-3 text-xl font-semibold text-[#1E3A8A] lg:text-2xl"
                        >
                            {CONTACT_EMAIL}
                            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                        </a>
                    </Reveal>
                </div>

                <Reveal delay={0.1}>
                    <div className="bg-[#0A1B3F] p-8 text-white shadow-[0_40px_80px_-40px_rgba(10,27,63,0.5)] lg:p-12">
                        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#7FD8C4]">
                            Talk to us
                        </p>
                        <h3 className="font-display mt-4 text-2xl font-semibold tracking-tight lg:text-3xl">
                            Request a demo
                        </h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-300">
                            We reply within one business day. No sales scripts — just a human.
                        </p>
                        <form data-testid="demo-request-form" onSubmit={submit} className="mt-10 space-y-6">
                            <Field label="Name" htmlFor="demo-name">
                                <input
                                    id="demo-name"
                                    data-testid="demo-form-name-input"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    className={INPUT_CLASS}
                                    placeholder="Your full name"
                                />
                            </Field>
                            <Field label="Work email" htmlFor="demo-email">
                                <input
                                    id="demo-email"
                                    data-testid="demo-form-email-input"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={set("email")}
                                    className={INPUT_CLASS}
                                    placeholder="you@company.com"
                                />
                            </Field>
                            <Field label="Company" htmlFor="demo-company">
                                <input
                                    id="demo-company"
                                    data-testid="demo-form-company-input"
                                    value={form.company}
                                    onChange={set("company")}
                                    className={INPUT_CLASS}
                                    placeholder="Organisation name"
                                />
                            </Field>
                            <Field label="Message" htmlFor="demo-message">
                                <textarea
                                    id="demo-message"
                                    data-testid="demo-form-message-input"
                                    required
                                    rows={4}
                                    value={form.message}
                                    onChange={set("message")}
                                    className={`${INPUT_CLASS} resize-none`}
                                    placeholder="Tell us about your team and what you'd like to see"
                                />
                            </Field>
                            <button
                                data-testid="demo-form-submit-button"
                                type="submit"
                                className="group inline-flex w-full items-center justify-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-[#0A1B3F] transition-colors duration-300 hover:bg-[#7FD8C4]"
                            >
                                Request a demo
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </button>
                        </form>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
